// Change #100463 - Back End > Company Details > Subscriptions > Add
// subscription. Creates N unassigned licences of one product for this company
// with a bespoke price each (via the Super Admin licence create). The company
// assigns them to people on its Users screen. Monthly / annual licences with a
// price are charged to the company's card on renewal by the nightly run.

import React, { useEffect, useMemo, useState } from "react";
import { Input } from "reactstrap";
import { toast } from "react-toastify";

import AppModal from "../../../../Components/Common/AppModal";
import { FetchAPI } from "../../../../helpers/APIdata/fetchAPI";
import { cardLabel } from "./EditProductModal";

const pad = (n) => String(n).padStart(2, "0");
const stamp = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
const today = () => new Date().toISOString().slice(0, 10);

export default function AddSubscriptionModal({ open, companyId, businessName, products = [], cards = [], billingReady = false, onClose, onDone }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ SubscriptionProduct_Id: "", Qty: "1", Price: "", Vat: "20", Duration: "1", StartDate: today(), Card: "" });
    }
  }, [open]);

  const product = useMemo(
    () => products.find((p) => String(p.Id) === String(form.SubscriptionProduct_Id)),
    [products, form.SubscriptionProduct_Id]
  );

  const pick = (id) => {
    const p = products.find((x) => String(x.Id) === String(id));
    setForm((f) => ({
      ...f,
      SubscriptionProduct_Id: id,
      Price: p?.AmountLocalSubTotal != null ? String(Number(p.AmountLocalSubTotal).toFixed(2)) : "",
      Duration: p?.SubscriptionLicenceDurationType_Id ? String(p.SubscriptionLicenceDurationType_Id) : f.Duration,
    }));
  };

  const qty = Math.max(0, parseInt(form.Qty, 10) || 0);
  const price = parseFloat(form.Price);
  const vatPct = parseFloat(form.Vat) || 0;
  const each = Number.isFinite(price) ? price : 0;
  const total = each * qty;

  const save = async () => {
    if (!form.SubscriptionProduct_Id) {
      toast.error("Pick a product");
      return;
    }
    if (qty < 1 || qty > 500) {
      toast.error("Enter a quantity from 1 to 500");
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      toast.error("Enter the price each (0 for free)");
      return;
    }
    const start = new Date(`${form.StartDate || today()}T00:00:00`);
    const now = new Date();
    if (start.toDateString() === now.toDateString()) start.setTime(now.getTime());
    const renewal = new Date(start);
    if (form.Duration === "2") renewal.setFullYear(renewal.getFullYear() + 1);
    else renewal.setMonth(renewal.getMonth() + 1);
    const grace = new Date(renewal);
    grace.setDate(grace.getDate() + 3);
    const vat = Math.round(each * vatPct) / 100;
    const row = {
      Company_Id: Number(companyId),
      SubscriptionProduct_Id: Number(form.SubscriptionProduct_Id),
      FlagAssigned: 0,
      SubscriptionLicenceDurationType_Id: Number(form.Duration),
      StartDateTime: stamp(start),
      RenewalDateTime: stamp(renewal),
      RenewalGraceDays: 3,
      RenewalGraceDateTime: stamp(grace),
      AmountLocalSubTotal: each.toFixed(2),
      AmountLocalVat: vat.toFixed(2),
      AmountLocalTotal: (each + vat).toFixed(2),
      AmountVatPercentage: vatPct.toFixed(2),
      PaymentRetryAttemptsMaximum: 3,
      PaymentRetryAttemptsRemaining: 3,
      // Change #100463 (delta 229) - charge a specific card; blank = primary.
      ...(billingReady && form.Card ? { BillingMasterPaymentCard_Id: Number(form.Card) } : {}),
    };
    setSaving(true);
    try {
      const res = await FetchAPI("/subscriptionlicenceadmin/create", "POST", Array.from({ length: qty }, () => ({ ...row })));
      if (Number(res?.status) === 200) {
        toast.success(`${qty} × ${product?.Name || "subscription"} added`);
        onDone && onDone();
      } else {
        toast.error(res?.message || "Could not add the subscription");
      }
    } catch (e) {
      toast.error("Could not add the subscription");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal
      open={open}
      onClose={() => !saving && onClose()}
      title="Add subscription"
      subtitle={businessName}
      icon="ri-add-circle-line"
      headerAccent="indigo"
      size="sm"
      onSubmit={save}
      submitLabel={qty > 1 ? `Add ${qty}` : "Add"}
      submitDisabled={saving}
      loading={saving}
      loadingText="Adding..."
    >
      <div className="row g-3">
        <div className="col-12">
          <label className="form-label mb-1">
            Product <span className="text-danger">*</span>
          </label>
          <Input type="select" value={form.SubscriptionProduct_Id || ""} onChange={(e) => pick(e.target.value)}>
            <option value="">Choose a product...</option>
            {products.map((p) => (
              <option key={p.Id} value={p.Id}>
                {p.Name} (#{p.Id})
                {p.AmountLocalSubTotal != null ? ` - £${Number(p.AmountLocalSubTotal).toFixed(2)}` : ""}
              </option>
            ))}
          </Input>
        </div>
        <div className="col-4">
          <label className="form-label mb-1">Quantity</label>
          <Input type="number" min={1} max={500} value={form.Qty || ""} onChange={(e) => setForm({ ...form, Qty: e.target.value })} />
        </div>
        <div className="col-8">
          <label className="form-label mb-1">Price each (ex VAT)</label>
          <div className="input-group">
            <span className="input-group-text">£</span>
            <Input
              type="number"
              min={0}
              step="0.01"
              value={form.Price ?? ""}
              onChange={(e) => setForm({ ...form, Price: e.target.value })}
            />
          </div>
        </div>
        <div className="col-4">
          <label className="form-label mb-1">VAT %</label>
          <Input type="number" min={0} step="0.01" value={form.Vat ?? ""} onChange={(e) => setForm({ ...form, Vat: e.target.value })} />
        </div>
        <div className="col-8">
          <label className="form-label mb-1">Billed</label>
          <Input type="select" value={form.Duration || "1"} onChange={(e) => setForm({ ...form, Duration: e.target.value })}>
            <option value="1">Monthly</option>
            <option value="2">Annually</option>
          </Input>
        </div>
        <div className="col-12">
          <label className="form-label mb-1">Charge to</label>
          <Input type="select" disabled={!billingReady} value={form.Card || ""} onChange={(e) => setForm({ ...form, Card: e.target.value })}>
            <option value="">The company's primary card</option>
            {cards.map((c) => (
              <option key={c.Id} value={String(c.Id)}>
                {cardLabel(c)}
                {Number(c.FlagPrimary) === 1 ? " (primary)" : ""}
              </option>
            ))}
          </Input>
        </div>
        <div className="col-12">
          <label className="form-label mb-1">Starts</label>
          <Input type="date" value={form.StartDate || ""} onChange={(e) => setForm({ ...form, StartDate: e.target.value })} />
        </div>
        <div className="col-12">
          <div className="alert alert-info fs-13 mb-0">
            {qty} × £{each.toFixed(2)} = <strong>£{total.toFixed(2)}</strong> ex VAT{" "}
            {form.Duration === "2" ? "per year" : "per month"}. They are added unassigned; the company gives them to
            people on its Users screen.{" "}
            {each > 0
              ? form.Duration === "2"
                ? "Note: the nightly card run only collects monthly licences, so an annual one is not charged automatically."
                : "Charged to the card on each monthly renewal from the start date."
              : ""}
          </div>
        </div>
      </div>
    </AppModal>
  );
}
